import { REVALIDATE_TIME } from "@/constants/cache"
import httpClient from "@/lib/http-client"
import { HeaderMenuPreferenceResponse, HeaderSettingsResponse } from "@/types/types"

/** Anonymous block-content reader for public Puck renderers. */
export const getPublicBlockContent = async (id: string) => {
  return httpClient.get<{ data: any }>(`/block-contents/${id}?populate=*`, null, {
    next: {
      revalidate: REVALIDATE_TIME,
      tags: [`block_content_${id}`],
    },
  })
}

/**
 * Fetch header template by ID
 * @param id - Template ID
 * @param lang - Language locale
 * @returns Header template data
 */
export const getHeaderAndFooterTheme = async (id: number, lang?: string) => {
  try {
    const response = await httpClient.get<{ data: any }>(`/block-contents/${id}?populate=*&locale=${lang}`, null, {
      next: {
        revalidate: REVALIDATE_TIME, // Cache for 1 hour - header/footer rarely change
        tags: [`header_template_${id}_${lang}`, `footer_template_${id}_${lang}`],
      },
    })

    if (response.data?.attributes) {
      return response.data
    }

    return null
  } catch (error) {
    console.error("❌ [Header Theme] Error fetching header theme:", error)
    return null
  }
}

/**
 * Fetch menu by name from menu preferences
 * @param name - Menu name
 * @param locale - Language locale
 * @returns Menu data
 */
export const getMenuById = async (id: number) => {
  try {
    const response = await httpClient.get<HeaderMenuPreferenceResponse>(`/menu-preferences/${id}`, null, {
      next: {
        revalidate: REVALIDATE_TIME, // Cache for 1 hour - menu rarely changes
        tags: [`menu_${id}`],
      },
    })
    const menuData = response.data || null
    return menuData
  } catch (error) {
    console.error("❌ [Menu Preference] Error fetching menu by name:", error)
    return null
  }
}

/**
 * Fetch currency rate from settings
 * @returns Currency rate setting
 */
export const getCurrencyRate = async () => {
  try {
    const response = await httpClient.get<HeaderSettingsResponse>(
      "/settings",
      {
        fields: ["key", "value"],
        "filters[key][$eq]": "REALTIME_CURRENCY_RATE",
      },
      {
        next: {
          revalidate: REVALIDATE_TIME, // 3h — freshness driven by Strapi stale-watermark, not by time
          tags: ["currency_rate"],
        },
      },
    )
    return response.data && response.data.length > 0 ? response.data[0] : null
  } catch (error) {
    console.error("Error fetching currency rate:", error)
    return null
  }
}
